import { useForm, useFormContext } from "react-hook-form";
import type { Patient, PatientInput } from "services/types/patients.type";
import { useUpdatePatientMutation } from "services/patients";
import React, { useCallback, useEffect, useMemo } from "react";
import { AppointmentFormValues } from "../appointment-form.component";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen,
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { useAppDispatch } from "app/hooks";

import UpdatePatientPreferredCommunicationMethodModal from "components/update-preferred-communication-method-modal/update-preffered-communication-method-modal";

interface AppointmentUpdatePatientPreferredCommunicationMethodModalProps {
  patient: Patient;
  isOpen: boolean;
  onCancel: () => void;
}

const AppointmentUpdatePatientPreferredCommunicationMethodModal: React.FC<
  AppointmentUpdatePatientPreferredCommunicationMethodModalProps
> = ({ patient, isOpen, onCancel }) => {
  const dispatch = useAppDispatch();

  const [updatePatient, { isLoading: isSubmitting }] =
    useUpdatePatientMutation();

  const { setValue: setParentFormValue } =
    useFormContext<AppointmentFormValues>();

  const defaultValues = useMemo(
    () => ({
      id: patient.id,
      preferred_communication_method:
        patient?.preferred_communication_method ?? "",
      do_not_mail: patient.do_not_mail,
      do_not_email: patient.do_not_email,
      do_not_message: patient.do_not_message,
      do_not_call: patient.do_not_call,
    }),
    [
      patient.id,
      patient?.preferred_communication_method,
      patient.do_not_mail,
      patient.do_not_email,
      patient.do_not_message,
      patient.do_not_call,
    ]
  );

  const { control, reset, handleSubmit } = useForm<PatientInput>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = useCallback(() => {
    return handleSubmit((data) => {
      return updatePatient(data).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
        } else {
          setParentFormValue(
            "patient.preferred_communication_method",
            response.data.preferred_communication_method
          );

          dispatch(
            setCustomMessage(
              `Patient preffered communication method was updated`
            )
          );
          onCancel();
        }
        dispatch(setSnackbarOpen(true));
      });
    })();
  }, [updatePatient, dispatch, handleSubmit, onCancel, setParentFormValue]);

  return (
    <UpdatePatientPreferredCommunicationMethodModal
      patient={patient}
      isOpen={isOpen}
      onCancel={onCancel}
      control={control}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
    />
  );
};

export default AppointmentUpdatePatientPreferredCommunicationMethodModal;
